import React, { useContext, useEffect, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import * as styles from '../check-in-employees.styles';
import EmployeeCard from '../cards/employee-card.component';
import CreateAgendaEntryModal from '../modals/create-agenda-entry-modal.component';
import MonthlyReservationOverviewModal from '../modals/monlty-reservation-overview-modal.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { Center } from '../../../core/components/center/center.component';
import Column from '../../../core/components/column/column.component';
import { MainAxisAlignment, CrossAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import SearchBar from '../../../core/components/search-bar/search-bar.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import type Office from '../../offices/models/office';
import type PermittedUser from '../../offices/models/permitted-user';
import Grid from '../../../core/components/grid/grid.component';

interface Props {
  readonly selectedOffice: Office | undefined;
  readonly allUsers: PermittedUser[] | undefined;
  readonly isLoading: boolean;
}
const EmployeeOverviewGroupOfficeManager = ({ selectedOffice, allUsers, isLoading }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [filteredPermittedUsers, setFilteredPermittedUsers] = useState<PermittedUser[]>(allUsers ?? []);
  const [selectedPermittedUser, setSelectedPermittedUser] = useState<PermittedUser | undefined>();

  const filterResults = (query: string): void => {
    let filteredUsers = new Array<PermittedUser>();

    if (selectedOffice !== undefined && allUsers != null && allUsers.length > 0) {
      filteredUsers = allUsers.filter((user) => user.email.toLowerCase().includes(query.toLowerCase()));
    }

    setFilteredPermittedUsers(filteredUsers);
  };

  const [showRegisterEmployeeModal, setShowRegisterEmployeeModal] = useState(false);
  const [showEmployeeDatePicker, setShowEmployeeDatePicker] = useState(false);
  const [selectedDates, setSelectedDates] = useState<Date[]>([new Date()]);

  const handleDatesChange = (dates: Date[]): void => {
    setSelectedDates(dates.map((d) => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))));
  };

  const onUserSelected = (permittedUser: PermittedUser): void => {
    setSelectedPermittedUser(permittedUser);
    setShowEmployeeDatePicker(true);
  };

  useEffect(() => {
    if (allUsers != null) {
      setFilteredPermittedUsers(allUsers);
    }
  }, [allUsers, setFilteredPermittedUsers]);

  return (
    <>
      <div className={style(styles.sectionCard(isMobile))}>
        <Row mainAxisAlignment={MainAxisAlignment.SpaceBetween} crossAxisAlignment={CrossAxisAlignment.Center}>
          <span className={style(styles.columnHeader(isMobile))}>
            {t('employees').toUpperCase()}: {selectedOffice?.name.toUpperCase()}
          </span>
          <Row itemSpacing={12} crossAxisAlignment={CrossAxisAlignment.Center}>
            <SearchBar onSearch={filterResults} placeholder={t('find-email').toString()} maxLength={100} />
          </Row>
        </Row>
        <Column>
          {((): React.JSX.Element => {
            if (isLoading && selectedOffice != null) {
              return (
                <Center>
                  <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
                </Center>
              );
            }

            if (selectedOffice == null) {
              return <span className={style(styles.emptyListMessage)}>{t('select-office-to-view-employees')}</span>;
            }

            if (allUsers?.length === 0) {
              return <span className={style(styles.emptyListMessage)}>{t('no-employees-available')}</span>;
            }

            return (
              <Grid
                rowClass={style(styles.gridRow)}
                numberOfColumns={isMobile ? 1 : 3}
                itemSpacing={12}
                numberOfItems={filteredPermittedUsers.length}
                renderItem={(index: number): React.JSX.Element | undefined => {
                  if (filteredPermittedUsers) {
                    const permittedUser = filteredPermittedUsers[index];
                    return (
                      permittedUser && (
                        <div className={isMobile ? style(styles.rowItem(true)) : undefined} key={permittedUser.id}>
                          <EmployeeCard
                            permittedUser={permittedUser}
                            onClick={(value): void => {
                              onUserSelected(value);
                            }}
                          />
                        </div>
                      )
                    );
                  }

                  return undefined;
                }}
              />
            );
          })()}
        </Column>
      </div>
      {selectedOffice != null && selectedPermittedUser != null && showEmployeeDatePicker && selectedOffice.id && (
        <MonthlyReservationOverviewModal
          showModal={showEmployeeDatePicker}
          handleClose={(): void => {
            setShowEmployeeDatePicker(false);
          }}
          user={selectedPermittedUser}
          officeId={selectedOffice.id}
          onDatesSelected={(dates: Date[]): void => {
            setShowEmployeeDatePicker(false);
            setSelectedDates(dates);
            setShowRegisterEmployeeModal(true);
          }}
        />
      )}
      {selectedOffice != null && selectedPermittedUser != null && showRegisterEmployeeModal && selectedOffice.id && (
        <CreateAgendaEntryModal
          showModal={showRegisterEmployeeModal}
          handleClose={(): void => {
            setShowRegisterEmployeeModal(false);
            setShowEmployeeDatePicker(true);
          }}
          permittedUser={selectedPermittedUser}
          officeId={selectedOffice.id}
          selectedDates={selectedDates}
          handleDatesChange={handleDatesChange}
        />
      )}
    </>
  );
};

export default EmployeeOverviewGroupOfficeManager;
